import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { KeyRound, ArrowLeft, Check } from "lucide-react";

export default function ResetPassword() {
  const { branding } = useTheme();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    if (pw.length < 8) { toast.error("Password must be 8+ characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Reset failed"));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md border border-border p-10 text-center" data-testid="reset-no-token">
          <h1 className="heading-display text-2xl mb-4">Reset link missing.</h1>
          <p className="text-sm text-foreground/70 mb-6">The link you opened doesn&apos;t include a valid reset token. Please request a new one.</p>
          <Link to="/forgot-password"><Button className="rounded-none">Request a new link</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-16">
      <div className="max-w-lg w-full" data-testid="reset-password-page">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-foreground/60 hover:text-foreground mb-8" data-testid="reset-back-login">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to log in
        </Link>
        {done ? (
          <div className="border border-border p-10" data-testid="reset-done-screen">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Check className="w-6 h-6 text-primary" />
            </div>
            <h1 className="heading-display text-3xl mb-3">Password updated.</h1>
            <p className="text-sm text-foreground/80">Redirecting you to log in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-border p-10">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-5">
              <KeyRound className="w-5 h-5 text-primary" />
            </div>
            <div className="label-eyebrow mb-2">{branding.platform_name}</div>
            <h1 className="heading-display text-3xl mb-6">Choose a new password.</h1>
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">New password</Label>
                <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="reset-pw-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Confirm new password</Label>
                <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11" data-testid="reset-pw2-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-none font-bold uppercase tracking-widest text-xs" data-testid="reset-submit-btn">
                {busy ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
